import { API_ORIGIN, MDASH_ORIGIN } from '../constant/apiHosts';
import { getBackend, BACKEND_MDASH } from './deviceBackend';

/**
 * Centralized WebSocket service for managing device notification connections
 *
 * Features:
 * - Connection pooling (one connection per device pubkey)
 * - Automatic reconnection on disconnect
 * - Subscription management for multiple components
 * - Fallback to polling after 3 consecutive failures
 * - One account-level socket (subscribeAccount) that carries every supaDash
 *   device of the account, for the Sites list
 */

/** Pool key prefix for account sockets; a device pubkey can never contain ':'. */
const ACCOUNT_KEY_PREFIX = 'account:';
class WebSocketService {
  constructor() {
    // Connection pool: { pubkey: { ws, subscribers, reconnectAttempts, fallbackMode } }
    this.connections = {};

    // Reconnection settings
    this.RECONNECT_DELAY = 1000; // 1 second
    this.MAX_RECONNECT_ATTEMPTS = 3;
    this.FALLBACK_POLL_INTERVAL = 30000; // 30 seconds
    // Heartbeat (compat-served sockets only — the compat room answers "ping"
    // with "pong" without waking). A socket the browser believes open but that
    // delivers nothing used to go unnoticed for the life of the tab, because
    // onclose never fires for it; now two unanswered pings close it, which
    // routes through the normal reconnect → fallback path. mDash's own socket
    // is left alone: its reaction to a text "ping" is not documented.
    this.HEARTBEAT_INTERVAL = 30000;
    this.HEARTBEAT_MAX_MISSED = 2;
    // A tab hidden at least this long reopens even sockets that look open on its
    // return: mDash's socket has no heartbeat and timers are throttled while
    // hidden, so nothing could have noticed one that stopped delivering. Long
    // enough that flicking between tabs stays clear of the compat layer's
    // 20 handshakes/min per token.
    this.REOPEN_AFTER_HIDDEN_MS = 30000;

    // Account subscription id → pool key, so unsubscribeAccount(id) needs no token.
    this.accountSubscriptions = {};
  }

  /**
   * Subscribe to device notifications
   * @param {string} pubkey - Device public key (access token)
   * @param {Function} onMessage - Callback when message received
   * @param {Function} onFallback - Callback when entering fallback mode (optional)
   * @returns {string} - Subscription ID for unsubscribing
   */
  subscribe(pubkey, onMessage, onFallback = null) {
    if (!pubkey) {
      return null;
    }
    return this._addSubscriber(pubkey, onMessage, onFallback, null);
  }

  /**
   * Subscribe to the account-level notify socket (2026-10-31).
   *
   * One socket per account carries a frame for every changed supaDash device,
   * so the Sites list does not need one socket per device (Clever Pub's head
   * office has 121 devices; the compat DO cannot hibernate while attached).
   * The compat layer serves it, so it gets the same heartbeat as a compat
   * device socket, and the same reconnect → fallback behaviour. Frames are
   * `{id, name: "updated", pubkey, data}`; pongs never reach subscribers.
   *
   * @param {string} token - Customer access token (what getToken() returns)
   * @param {Function} onMessage - Callback per frame
   * @param {Function} onFallback - Callback when the socket could not be kept open
   * @param {Function} onRecover - Callback when the socket is open again after a fallback (optional)
   * @returns {string|null} - Subscription ID for unsubscribeAccount()
   */
  subscribeAccount(token, onMessage, onFallback = null, onRecover = null) {
    if (!token) {
      return null;
    }
    const key = ACCOUNT_KEY_PREFIX + token;
    const subscriptionId = this._addSubscriber(key, onMessage, onFallback, onRecover, {
      isAccount: true,
      token,
    });
    this.accountSubscriptions[subscriptionId] = key;
    return subscriptionId;
  }

  /**
   * Unsubscribe from the account-level socket.
   * @param {string} subscriptionId - Subscription ID from subscribeAccount()
   */
  unsubscribeAccount(subscriptionId) {
    const key = this.accountSubscriptions[subscriptionId];
    if (!key) return;
    delete this.accountSubscriptions[subscriptionId];
    this.unsubscribe(key, subscriptionId);
  }

  /**
   * Shared subscribe path: create the pooled connection on first use, then
   * register the subscriber.
   * @private
   */
  _addSubscriber(key, onMessage, onFallback, onRecover, accountTarget = null) {
    // Generate unique subscription ID
    const subscriptionId = `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Create or get existing connection
    if (!this.connections[key]) {
      this.connections[key] = {
        ws: null,
        subscribers: {},
        reconnectAttempts: 0,
        fallbackMode: false,
        fallbackInterval: null,
        reconnectTimer: null,
        heartbeatTimer: null,
        missedPongs: 0,
        account: accountTarget,
      };
      this._connect(key);
    }

    // Add subscriber
    this.connections[key].subscribers[subscriptionId] = {
      onMessage,
      onFallback: onFallback || (() => {}),
      onRecover: onRecover || (() => {}),
    };

    // If already in fallback mode, notify this subscriber
    if (this.connections[key].fallbackMode && onFallback) {
      onFallback();
    }

    return subscriptionId;
  }

  /**
   * Unsubscribe from device notifications
   * @param {string} pubkey - Device public key
   * @param {string} subscriptionId - Subscription ID from subscribe()
   */
  unsubscribe(pubkey, subscriptionId) {
    if (!pubkey || !subscriptionId) return;

    const conn = this.connections[pubkey];
    if (!conn) return;

    delete conn.subscribers[subscriptionId];

    // If no more subscribers, close connection
    if (Object.keys(conn.subscribers).length === 0) {
      this._disconnect(pubkey);
      delete this.connections[pubkey];
    }
  }

  /**
   * Connect to device WebSocket
   * @private
   */
  _connect(pubkey) {
    const conn = this.connections[pubkey];
    if (!conn) return;

    // Clear any existing reconnect timer
    if (conn.reconnectTimer) {
      clearTimeout(conn.reconnectTimer);
      conn.reconnectTimer = null;
    }

    // Build WebSocket URL.
    //
    // mDash-backed devices connect straight to mdash.net, exactly as this app
    // did before the 2026-09-01 cutover (mDash accepts any Origin and
    // WebSockets are not CORS-gated). Routing them through the compat layer
    // instead pins a Durable Object open for the life of the socket, and that
    // DO cannot hibernate while a client is attached — it is the account's only
    // metered cost. supaDash-backed devices, and devices whose backend is not
    // known yet, use API_ORIGIN: the compat layer serves the notify socket
    // correctly for both, so an unknown backend is safe, merely not free.
    //
    // Only the WebSocket host varies. REST always goes to API_ORIGIN.
    //
    // NOTE: the host is resolved per connection attempt, not per pubkey for the
    // life of the app. If a device's backend becomes known AFTER a socket to
    // API_ORIGIN is already open, the open socket is deliberately left alone —
    // tearing down a healthy connection to save cost would churn the notify
    // stream (and trip the reconnect/fallback counters) for no user-visible
    // gain. The next reconnect picks up the newly-known host on its own.
    //
    // The account socket is compat-only (it multiplexes supaDash devices, which
    // mDash cannot serve), so it always uses API_ORIGIN and always heartbeats.
    const isAccount = Boolean(conn.account);
    const origin = !isAccount && getBackend(pubkey) === BACKEND_MDASH ? MDASH_ORIGIN : API_ORIGIN;
    const wsBase = origin.replace(/^http/, 'ws');
    const wsUrl = isAccount
      ? `${wsBase}/api/v2/m/customer/notify?access_token=${encodeURIComponent(conn.account.token)}`
      : `${wsBase}/api/v2/m/device/notify?access_token=${encodeURIComponent(pubkey)}`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        // console.log(`[WebSocketService] Connected to ${pubkey.substr(0, 8)}...`);
        conn.reconnectAttempts = 0;
        if (origin === API_ORIGIN) this._startHeartbeat(pubkey, ws);

        // Exit fallback mode if we were in it
        if (conn.fallbackMode) {
          this._exitFallbackMode(pubkey);
        }
      };

      ws.onmessage = (ev) => {
        if (ev.data === 'pong') {
          conn.missedPongs = 0;
          return;
        }
        let msg = null;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }

        // Notify all subscribers
        Object.values(conn.subscribers).forEach(sub => {
          try {
            sub.onMessage(msg);
          } catch {
            // ignore subscriber callback errors
          }
        });
      };

      ws.onerror = () => {};

      ws.onclose = () => {
        // A socket already replaced (restoreConnections, or a fallback retry)
        // must not clear its successor or start a second reconnect.
        if (conn.ws !== ws) return;
        conn.ws = null;
        this._stopHeartbeat(pubkey);

        // Only reconnect if we still have subscribers
        if (Object.keys(conn.subscribers).length > 0) {
          this._handleReconnect(pubkey);
        }
      };

      conn.ws = ws;
    } catch {
      this._handleReconnect(pubkey);
    }
  }

  /** @private */
  _startHeartbeat(pubkey, ws) {
    const conn = this.connections[pubkey];
    if (!conn) return;
    this._stopHeartbeat(pubkey);
    conn.missedPongs = 0;
    conn.heartbeatTimer = setInterval(() => {
      if (conn.ws !== ws) {
        this._stopHeartbeat(pubkey);
        return;
      }
      if (conn.missedPongs >= this.HEARTBEAT_MAX_MISSED) {
        // Half-open: close it ourselves so onclose runs the reconnect path.
        this._stopHeartbeat(pubkey);
        try {
          ws.close(4000, 'heartbeat timeout');
        } catch {
          // ignore close errors
        }
        return;
      }
      conn.missedPongs++;
      try {
        ws.send('ping');
      } catch {
        // a send failure surfaces as a missed pong on the next tick
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  /** @private */
  _stopHeartbeat(pubkey) {
    const conn = this.connections[pubkey];
    if (!conn || !conn.heartbeatTimer) return;
    clearInterval(conn.heartbeatTimer);
    conn.heartbeatTimer = null;
  }

  /**
   * Handle reconnection logic
   * @private
   */
  _handleReconnect(pubkey) {
    const conn = this.connections[pubkey];
    if (!conn) return;

    conn.reconnectAttempts++;

    // Check if we should enter fallback mode
    if (conn.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
      this._enterFallbackMode(pubkey);
      return;
    }

    // Schedule reconnection
    conn.reconnectTimer = setTimeout(() => {
      this._connect(pubkey);
    }, this.RECONNECT_DELAY);
  }

  /**
   * Enter fallback polling mode
   * @private
   */
  _enterFallbackMode(pubkey) {
    const conn = this.connections[pubkey];
    if (!conn || conn.fallbackMode) return;

    conn.fallbackMode = true;

    // Notify all subscribers to start polling
    Object.values(conn.subscribers).forEach(sub => {
      try {
        sub.onFallback();
      } catch {
        // ignore fallback callback errors
      }
    });

    // Set up periodic reconnection attempts (every 30 seconds)
    conn.fallbackInterval = setInterval(() => {
      conn.reconnectAttempts = 0; // Reset counter for new attempt
      this._connect(pubkey);
    }, this.FALLBACK_POLL_INTERVAL);
  }

  /**
   * Exit fallback polling mode
   * @private
   */
  _exitFallbackMode(pubkey) {
    const conn = this.connections[pubkey];
    if (!conn || !conn.fallbackMode) return;

    conn.fallbackMode = false;

    // Clear fallback interval
    if (conn.fallbackInterval) {
      clearInterval(conn.fallbackInterval);
      conn.fallbackInterval = null;
    }

    // Tell subscribers the socket is the feed again (the account subscriber
    // hands polling back to it).
    Object.values(conn.subscribers).forEach(sub => {
      try {
        sub.onRecover();
      } catch {
        // ignore recover callback errors
      }
    });
  }

  /**
   * Restore connections when the tab returns to the foreground: reconnect every
   * socket that is down without waiting for its retry timer, and after a long
   * enough absence reopen the ones that look open too. A socket still connecting
   * is left to finish.
   * @param {number} hiddenForMs - How long the tab was in the background
   */
  restoreConnections(hiddenForMs) {
    const reopenOpenSockets = hiddenForMs >= this.REOPEN_AFTER_HIDDEN_MS;
    Object.keys(this.connections).forEach((key) => {
      const conn = this.connections[key];
      const state = conn.ws ? conn.ws.readyState : WebSocket.CLOSED;
      if (state === WebSocket.CONNECTING) return;
      if (state === WebSocket.OPEN && !reopenOpenSockets) return;
      this._reconnectNow(key);
    });
  }

  /** @private */
  _reconnectNow(key) {
    const conn = this.connections[key];
    const previous = conn.ws;
    conn.ws = null; // before close(), so its onclose sees it was replaced
    conn.reconnectAttempts = 0;
    this._stopHeartbeat(key);
    if (previous) {
      try {
        previous.close();
      } catch {
        // ignore close errors
      }
    }
    this._connect(key); // also cancels a pending reconnect timer
  }

  /**
   * Disconnect from device WebSocket
   * @private
   */
  _disconnect(pubkey) {
    const conn = this.connections[pubkey];
    if (!conn) return;

    // Clear timers
    if (conn.reconnectTimer) {
      clearTimeout(conn.reconnectTimer);
      conn.reconnectTimer = null;
    }

    if (conn.fallbackInterval) {
      clearInterval(conn.fallbackInterval);
      conn.fallbackInterval = null;
    }
    this._stopHeartbeat(pubkey);

    // Close WebSocket
    if (conn.ws) {
      try {
        conn.ws.close();
      } catch {
        // ignore close errors
      }
      conn.ws = null;
    }
  }

  /**
   * Get connection status
   * @param {string} pubkey - Device public key (or an `account:<token>` pool key; see getAccountStatus)
   * @returns {string} - 'connected', 'connecting', 'fallback', or 'disconnected'
   */
  getStatus(pubkey) {
    const conn = this.connections[pubkey];
    if (!conn) return 'disconnected';
    if (conn.fallbackMode) return 'fallback';
    if (conn.ws && conn.ws.readyState === WebSocket.OPEN) return 'connected';
    if (conn.ws && conn.ws.readyState === WebSocket.CONNECTING) return 'connecting';
    return 'disconnected';
  }

  /**
   * Connection status of the account-level socket.
   * @param {string} token - Customer access token passed to subscribeAccount
   * @returns {string} - same values as getStatus
   */
  getAccountStatus(token) {
    return this.getStatus(ACCOUNT_KEY_PREFIX + token);
  }

  /**
   * Cleanup all connections (for app shutdown)
   */
  cleanup() {
    Object.keys(this.connections).forEach(pubkey => {
      this._disconnect(pubkey);
    });
    this.connections = {};
    this.accountSubscriptions = {};
  }
}

// Export singleton instance
export default new WebSocketService();
